import React from "react";
import type { InvitationsState } from "./useInvitationsState";

interface WizardStepOneProps {
  state: InvitationsState;
}

const WizardStepOne: React.FC<WizardStepOneProps> = ({ state }) => {
  const {
    newInvite,
    formAssessment,
    isIndividual,
    entitySubType,
    RISK_BADGE_CONFIG,
    formatABN,
    setWizardStep,
  } = state;

  if (!formAssessment) return null;

  const typeMap: Record<string, { icon: string; label: string; desc: string }> =
    {
      individual: {
        icon: "👤",
        label: "Individual",
        desc: "Natural person — no ABN/ACN required",
      },
      "sole-trader": {
        icon: "🧑‍💼",
        label: "Sole Trader",
        desc: "Individual operating a business",
      },
      sole_trader: {
        icon: "🧑‍💼",
        label: "Sole Trader",
        desc: "Individual operating a business",
      },
      company: {
        icon: "🏢",
        label: "Company",
        desc: "Registered company or body corporate",
      },
      body_corporate: {
        icon: "🏢",
        label: "Company",
        desc: "Registered company or body corporate",
      },
      partnership: {
        icon: "🤝",
        label: "Partnership",
        desc: "Two or more persons carrying on business together",
      },
      trust: {
        icon: "📋",
        label: "Trust",
        desc: "Trust structure with trustees and beneficiaries",
      },
      association: {
        icon: "🏛️",
        label: "Association",
        desc: "Incorporated or unincorporated association",
      },
      government: {
        icon: "🏛️",
        label: "Government Body",
        desc: "Government entity or statutory body",
      },
    };

  const c2Type = (() => {
    if (isIndividual && !formAssessment?.entityAbn) return "individual";
    if (entitySubType && typeMap[entitySubType]) return entitySubType;
    const stored = formAssessment?.c2EntityType?.toLowerCase();
    if (stored && typeMap[stored]) return stored;
    return formAssessment?.entityAbn ? "company" : "individual";
  })();
  const typeInfo = typeMap[c2Type];

  return (
    <div>
      <h3
        style={{
          marginBottom: 6,
          fontSize: 16,
          color: "#111",
          fontWeight: 600,
        }}
      >
        Step 1 — C2 Customer Details
      </h3>
      <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
        Entity details from the linked Risk Assessment. Confirm and continue.
      </p>

      {/* Entity Name (read-only from assessment) */}
      <div style={{ marginBottom: 22 }}>
        <label
          style={{
            display: "block",
            color: "#64748B",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase" as const,
            marginBottom: 8,
          }}
        >
          C2 — Customer Full Legal Name
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "14px 18px",
            background: "#EFF6FF",
            border: "1.5px solid #BFDBFE",
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 22 }}>🏷️</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#1B3A6B", fontSize: 15, fontWeight: 700 }}>
              {newInvite.organisationName || "—"}
            </div>
            {formAssessment?.entityAbn && (
              <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>
                ABN: {formatABN(formAssessment.entityAbn)}
              </div>
            )}
          </div>
          <div
            style={{
              background: "#DBEAFE",
              borderRadius: 6,
              padding: "3px 10px",
              fontSize: 10,
              fontWeight: 700,
              color: "#1E40AF",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            From Assessment
          </div>
        </div>
      </div>

      {/* Entity Type (read-only from assessment) */}
      <div style={{ marginBottom: 22 }}>
        <label
          style={{
            display: "block",
            color: "#64748B",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase" as const,
            marginBottom: 8,
          }}
        >
          C2 — Customer Entity Type
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "14px 18px",
            background: "#EFF6FF",
            border: "1.5px solid #BFDBFE",
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 28 }}>{typeInfo.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#1B3A6B", fontSize: 14, fontWeight: 700 }}>
              {typeInfo.label}
            </div>
            <div style={{ color: "#64748B", fontSize: 11, marginTop: 1 }}>
              {typeInfo.desc}
            </div>
          </div>
          <div
            style={{
              background: "#DBEAFE",
              borderRadius: 6,
              padding: "3px 10px",
              fontSize: 10,
              fontWeight: 700,
              color: "#1E40AF",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            From Assessment
          </div>
        </div>
      </div>

      {/* Risk Rating badge */}
      <div style={{ marginBottom: 22 }}>
        <label
          style={{
            display: "block",
            color: "#64748B",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase" as const,
            marginBottom: 8,
          }}
        >
          Risk Rating
        </label>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            background: RISK_BADGE_CONFIG[formAssessment.riskRating].bg,
            color: RISK_BADGE_CONFIG[formAssessment.riskRating].color,
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {formAssessment.riskRating === "HIGH"
            ? "🔴"
            : formAssessment.riskRating === "MEDIUM"
              ? "🟡"
              : "🟢"}{" "}
          {RISK_BADGE_CONFIG[formAssessment.riskRating].label}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 8,
        }}
      >
        <button
          type="button"
          className="btn-cancel"
          onClick={state.handleCloseForm}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-submit"
          onClick={() => setWizardStep(2)}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default WizardStepOne;
